import { useMemo, useState } from "react";

import { formattedDataSelect } from "@app/common/utils/utils";
import { GCidAccount } from "@app/config/globalsConst";
import { useGetBankEntities, useGetDepositType, useGetFranchise } from "@app/services/common/hooks";
import { generateId } from "@app/utils/generateId";
import {
  Box,
  Button,
  Card,
  Grid,
  NumberFormatter,
  NumberInput,
  Select,
  Text,
  TextInput
} from "@mantine/core";
import {
  IconCardsFilled,
  IconCashBanknote,
  IconCashRegister,
  IconCirclePlus,
  IconCreditCard,
  IconReceipt,
  IconWallet,
  IconX
} from "@tabler/icons-react";
import { IDeposit, IDepositState, PaymentMethods } from "./depositComponentInter";

const icons: PaymentMethods = {
  transfer: <IconCashRegister style={{ width: 30, height: 30 }} />,
  cash: <IconWallet style={{ width: 30, height: 30 }} />,
  card: <IconCardsFilled style={{ width: 30, height: 30 }} />,
  debit_note: <IconCashBanknote style={{ width: 30, height: 30 }} />,
  credit_note: <IconCreditCard style={{ width: 30, height: 30 }} />,
  check: <IconReceipt style={{ width: 30, height: 30 }} />,
  wallet: <IconWallet style={{ width: 30, height: 30 }} />
};

const YES_NO_OPTIONS = [
  { label: "Seleccionar...", value: "" },
  { label: "Sí", value: "1" },
  { label: "No", value: "0" }
];

const INITIAL_STATE = {
  depositTypeId: "",
  amount: "",
  pendingAmount: "",
  type: "default",
  bankId: null,
  franchiseId: null,
  terminalId: null,
  referenceNumber: "",
  donate: null,
  donationValue: ""
};

export default function DepositComponent({ payment = 0, onChange, walletAmount }: IDeposit) {
  const [depositList, setDepositList] = useState<IDepositState[]>([
    { ...INITIAL_STATE, id: generateId() }
  ]);
  const hasWalletAmount = Number(walletAmount) > 0;

  const normalizeDepositForOutput = (deposit: IDepositState): IDepositState => {
    if (deposit.type !== "cash") {
      return deposit;
    }

    const amount = Number(deposit.amount || 0);
    const pendingAmount = Number(deposit.pendingAmount || 0);
    const donationValue = Number(deposit.donationValue || 0);

    return {
      ...deposit,
      amount: Math.max(amount - pendingAmount - donationValue, 0)
    };
  };

  const emitChange = (state: IDepositState[]) => {
    onChange(state.map(normalizeDepositForOutput));
  };

  const {
    data: depositTypesList = {
      results: [],
      rowTotal: 0
    }
  } = useGetDepositType({ eaccount: GCidAccount(), active: 1 });

  const {
    data: bankList = {
      results: [],
      rowTotal: 0
    },
    refetch: refetchBankList
  } = useGetBankEntities({ eaccount: GCidAccount() });

  const {
    data: franchiseList = {
      results: [],
      rowTotal: 0
    },
    refetch: refetchFranchise
  } = useGetFranchise({ eaccount: GCidAccount() });

  const depositTypes = useMemo(() => {
    if (depositTypesList?.results) {
      return [
        ...formattedDataSelect(depositTypesList.results, "depoTypeName", "depoTypeLabel", [
          "depoTypeId"
        ]).filter((type) => hasWalletAmount || type.value !== "wallet")
      ];
    }
    return [];
  }, [depositTypesList?.results, hasWalletAmount]);

  type IOptionItem = (typeof depositTypes)[number] & { depoTypeId?: string };

  const banks = formattedDataSelect(bankList.results, "description", "id");
  const franchises = formattedDataSelect(franchiseList.results, "fraName", "fraId");

  const getTerminalsForDeposit = (deposit: IDepositState) => {
    const franchise = franchiseList.results?.find(
      (fra) => String(fra.fraId) === String(deposit.franchiseId)
    );
    const enabledTerminals = franchise?.terminals?.filter((t) => t.terminalEnabled === "1") ?? [];
    return formattedDataSelect(enabledTerminals, "terminalName", "terminalId");
  };

  const totalPayment = useMemo(() => {
    if (depositList.length) {
      return depositList.reduce((acc, el) => {
        return acc + (isNaN(Number(el.amount)) ? 0 : Number(el.amount));
      }, 0);
    }
    return 0;
  }, [depositList]);

  const onAddNewDeposit = () => {
    const options = [...depositList, { ...INITIAL_STATE, id: generateId() }];
    setDepositList(options);
    emitChange(options);
  };

  const onRemoveDeposit = (id: string) => {
    const options = depositList.filter((deposit) => deposit.id !== id);
    setDepositList(options);
    emitChange(options);
  };

  const onChangeDepositType = (option: { id: string; type: string; deposit: IDepositState }) => {
    if (option.type === "transfer" || option.type === "card") {
      refetchBankList();
    }

    if (option.type === "card") {
      refetchFranchise();
    }

    const options = depositList.map((dep) => {
      if (dep.id === option.deposit.id) {
        return {
          id: dep.id,
          ...INITIAL_STATE,
          type: option.type,
          depositTypeId: option.id,
          amount: ""
        };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeBank = (bankId: number, depositId: string) => {
    const options = depositList.map((dep) => {
      if (dep.id === depositId) {
        return { ...dep, bankId };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeFranchise = (franchiseId: number, depositId: string) => {
    const selectedFranchise = franchiseList.results?.find(
      (fra) => String(fra.fraId) === String(franchiseId)
    );
    const bankId = selectedFranchise?.niifAccountBankEntityId
      ? Number(selectedFranchise.niifAccountBankEntityId)
      : null;
    const options = depositList.map((dep) => {
      if (dep.id === depositId) {
        return { ...dep, franchiseId, terminalId: null, bankId };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeTerminal = (terminalId: string, depositId: string) => {
    const options = depositList.map((dep) => {
      if (dep.id === depositId) {
        return { ...dep, terminalId };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeAmount = (value: number, deposit: IDepositState) => {
    const options = depositList.map((dep) => {
      if (dep.id === deposit.id) {
        return {
          ...dep,
          amount: value,
          pendingAmount:
            deposit.type === "cash" && value > payment
              ? value - payment - (Number(deposit?.donationValue) ?? 0)
              : "",
          returnedChange: "",
          donate: null,
          donationValue: ""
        };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeDonation = (value: string, depositId: string) => {
    const options = depositList.map((dep) => {
      if (dep.id === depositId) {
        return {
          ...dep,
          donate: value,
          donationValue: "",
          pendingAmount: value === "0" ? Number(dep.amount) - payment : dep.pendingAmount
        };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeReturnedChange = (value: string, deposit: IDepositState) => {
    const options = depositList.map((dep) => {
      if (dep.id === deposit.id) {
        return {
          ...dep,
          returnedChange: value
        };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeDonationValue = (value: string, deposit: IDepositState) => {
    const options = depositList.map((dep) => {
      if (dep.id === deposit.id) {
        return {
          ...dep,
          donationValue: value,
          pendingAmount: Number(deposit?.amount) - payment - (Number(value) ?? 0)
        };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const onChangeRefNum = (value: string, depositId: string) => {
    const options = depositList.map((dep) => {
      if (dep.id === depositId) {
        return { ...dep, referenceNumber: value };
      }
      return dep;
    });
    setDepositList(options);
    emitChange(options);
  };

  const renderDefaultType = () => {
    return (
      <>
        <Grid.Col pb={0} span={6} className="self-end">
          <TextInput
            className=" w-100"
            disabled={true}
            label={" "}
            data-testid="depositcomponent-default-input-1"
          />
        </Grid.Col>
        <Grid.Col pb={0} span={6} className="self-end">
          <TextInput
            className=" w-100"
            disabled={true}
            label={" "}
            data-testid="depositcomponent-default-input-2"
          />
        </Grid.Col>
        <Grid.Col pb={0} span={6} className="self-end">
          <TextInput
            className=" w-100"
            disabled={true}
            label={" "}
            data-testid="depositcomponent-default-input-3"
          />
        </Grid.Col>
      </>
    );
  };

  const renderWalletType = (deposit: IDepositState) => {
    return (
      <Grid.Col pb={0} span={6} className="self-end">
        <NumberInput
          label="Monto pagado"
          required
          className=" w-100"
          allowNegative={false}
          placeholder="Escribe..."
          decimalScale={0}
          prefix="$"
          thousandSeparator={","}
          decimalSeparator={"."}
          allowLeadingZeros={false}
          value={deposit?.amount}
          data-testid="depositcomponent-wallet-amount-input"
          onValueChange={({ floatValue }) => {
            const value = floatValue ?? 0;
            void onChangeAmount(value, deposit);
          }}
          isAllowed={({ floatValue }) => {
            const value = floatValue ?? 0;
            return value <= Number(walletAmount);
          }}
        />
      </Grid.Col>
    );
  };

  const renderTransferType = (deposit: IDepositState) => {
    return (
      <>
        <Grid.Col pb={0} span={6} className="self-end">
          <NumberInput
            label="Monto pagado"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            prefix="$"
            thousandSeparator={","}
            decimalSeparator={"."}
            allowLeadingZeros={false}
            value={deposit?.amount}
            data-testid="depositcomponent-transfer-amount-input"
            onValueChange={({ floatValue }) => {
              const value = floatValue ?? 0;
              void onChangeAmount(value, deposit);
            }}
            isAllowed={({ floatValue }) => {
              const value = floatValue ?? 0;
              return value <= payment;
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={6}>
          <Select
            searchable
            className="text-secondary"
            label="Banco"
            required
            placeholder={"Seleccionar..."}
            data={banks}
            value={banks.find((bank) => Number(bank.value) === deposit?.bankId)?.value}
            data-testid="depositcomponent-transfer-bank-select"
            onChange={(option) => {
              onChangeBank(Number(option), deposit.id);
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={6} className="self-end">
          <NumberInput
            label="No. referencia"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            allowLeadingZeros
            value={deposit?.referenceNumber}
            data-testid="depositcomponent-transfer-reference-input"
            onValueChange={({ value }) => {
              void onChangeRefNum(value, deposit.id);
            }}
          />
        </Grid.Col>
      </>
    );
  };

  const renderCashType = (deposit: IDepositState) => {
    return (
      <>
        <Grid.Col pb={0} span={6} className="self-end">
          <NumberInput
            label="Monto pagado"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            prefix="$"
            thousandSeparator={","}
            decimalSeparator={"."}
            allowLeadingZeros={false}
            value={deposit?.amount}
            data-testid="depositcomponent-cash-amount-input"
            onValueChange={({ floatValue }) => {
              const value = floatValue ?? 0;
              void onChangeAmount(value, deposit);
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={4} className=" pt-2">
          <small className={"secondary-color ms-1 d-block"}>Vuelto total</small>
          <NumberInput
            disabled
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            prefix="$"
            thousandSeparator={","}
            decimalSeparator={"."}
            allowLeadingZeros={false}
            value={Number(deposit?.amount) > payment ? Number(deposit?.pendingAmount) : 0}
            data-testid="depositcomponent-cash-change-total-input"
          />
        </Grid.Col>
        {Number(deposit?.amount) > payment && (
          <>
            <Grid.Col pb={0} span={4}>
              <small className={"secondary-color ms-1 d-block"}>¿Donar vuelto?</small>
              <Select
                className="text-secondary"
                placeholder={"Seleccionar..."}
                value={YES_NO_OPTIONS.find((opt) => opt.value === deposit?.donate)?.value}
                data={YES_NO_OPTIONS}
                data-testid="depositcomponent-cash-donate-select"
                onChange={(option) => {
                  onChangeDonation(option ?? "", deposit.id);
                }}
              />
            </Grid.Col>
            {deposit?.donate === "1" && (
              <Grid.Col pb={0} span={4}>
                <small className={"secondary-color ms-1 d-block"}>¿Cuánto?</small>
                <NumberInput
                  className=" w-100"
                  allowNegative={false}
                  decimalScale={0}
                  prefix="$"
                  thousandSeparator={","}
                  decimalSeparator={"."}
                  allowLeadingZeros={false}
                  value={deposit?.donationValue}
                  data-testid="depositcomponent-cash-donation-amount-input"
                  onValueChange={({ floatValue }) => {
                    onChangeDonationValue(String(floatValue ?? 0), deposit);
                  }}
                  isAllowed={({ floatValue }) => {
                    const value = floatValue ?? "0";
                    return (
                      Number(value) <=
                      Number(deposit?.amount) - payment - (Number(deposit?.donationValue) ?? 0)
                    );
                  }}
                />
              </Grid.Col>
            )}
            <Grid.Col pb={0} span={4}>
              <small className={"secondary-color ms-1 d-block"}>¿Vuelto entregado?</small>
              <Select
                className="text-secondary"
                placeholder={"Seleccionar..."}
                data={YES_NO_OPTIONS}
                value={YES_NO_OPTIONS.find((opt) => opt.value === deposit?.returnedChange)?.value}
                data-testid="depositcomponent-cash-change-delivered-select"
                onChange={(option) => {
                  onChangeReturnedChange(option ?? "", deposit);
                }}
              />
            </Grid.Col>
          </>
        )}
      </>
    );
  };

  const renderCardType = (deposit: IDepositState) => {
    return (
      <>
        <Grid.Col pb={0} span={6} className="self-end">
          <NumberInput
            label="Monto pagado"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            prefix="$"
            thousandSeparator={","}
            decimalSeparator={"."}
            allowLeadingZeros={false}
            value={deposit?.amount}
            data-testid="depositcomponent-card-amount-input"
            onValueChange={({ floatValue }) => {
              const value = floatValue ?? 0;
              void onChangeAmount(value, deposit);
            }}
            isAllowed={({ floatValue }) => {
              const value = floatValue ?? 0;
              return value <= payment;
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={4}>
          <Select
            searchable
            label="Franquicia"
            required
            className="text-secondary"
            placeholder={"Seleccionar..."}
            data={franchises}
            value={franchises.find((fra) => Number(fra.value) === deposit?.franchiseId)?.value}
            data-testid="depositcomponent-card-franchise-select"
            onChange={(option) => {
              onChangeFranchise(Number(option), deposit.id);
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={4}>
          <Select
            searchable
            label="Terminal"
            required
            className="text-secondary"
            placeholder={"Seleccionar..."}
            data={getTerminalsForDeposit(deposit)}
            value={
              getTerminalsForDeposit(deposit).find((t) => t.value === deposit?.terminalId)?.value ??
              null
            }
            disabled={!deposit?.franchiseId}
            data-testid="depositcomponent-card-terminal-select"
            onChange={(option) => {
              onChangeTerminal(option ?? "", deposit.id);
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={4}>
          <Select
            searchable
            label="Banco"
            required
            className="text-secondary"
            placeholder={"Seleccionar..."}
            data={banks.filter((bank) => {
              const selectedFranchise = franchiseList.results?.find(
                (fra) => String(fra.fraId) === String(deposit.franchiseId)
              );
              if (selectedFranchise?.niifAccountBankEntityId) {
                return String(bank.value) === String(selectedFranchise.niifAccountBankEntityId);
              }
              return true;
            })}
            value={banks.find((bank) => Number(bank.value) === deposit?.bankId)?.value}
            disabled={!!deposit?.franchiseId}
            data-testid="depositcomponent-card-bank-select"
            onChange={(option) => {
              onChangeBank(Number(option), deposit.id);
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={4} className="self-end">
          <NumberInput
            label="No. referencia"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            allowLeadingZeros
            value={deposit?.referenceNumber}
            data-testid="depositcomponent-card-reference-input"
            onValueChange={({ value }) => {
              void onChangeRefNum(value, deposit.id);
            }}
          />
        </Grid.Col>
      </>
    );
  };

  const renderNoteType = (deposit: IDepositState) => {
    return (
      <Grid.Col pb={0} span={6} className="self-end">
        <NumberInput
          label="Monto pagado"
          required
          className=" w-100"
          allowNegative={false}
          placeholder="Escribe..."
          decimalScale={0}
          prefix="$"
          thousandSeparator={","}
          decimalSeparator={"."}
          allowLeadingZeros={false}
          value={deposit?.amount}
          data-testid="depositcomponent-note-amount-input"
          onValueChange={({ floatValue }) => {
            const value = floatValue ?? 0;
            void onChangeAmount(value, deposit);
          }}
          isAllowed={({ floatValue }) => {
            const value = floatValue ?? 0;
            return value <= payment;
          }}
        />
      </Grid.Col>
    );
  };

  const renderCheckType = (deposit: IDepositState) => {
    return (
      <>
        <Grid.Col pb={0} span={6} className="self-end">
          <NumberInput
            label="Monto pagado"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            prefix="$"
            thousandSeparator={","}
            decimalSeparator={"."}
            allowLeadingZeros={false}
            value={deposit?.amount}
            data-testid="depositcomponent-check-amount-input"
            onValueChange={({ floatValue }) => {
              const value = floatValue ?? 0;
              void onChangeAmount(value, deposit);
            }}
            isAllowed={({ floatValue }) => {
              const value = floatValue ?? 0;
              return value <= payment;
            }}
          />
        </Grid.Col>
        <Grid.Col pb={0} span={6} className="self-end">
          <NumberInput
            label="No. referencia"
            required
            className=" w-100"
            allowNegative={false}
            placeholder="Escribe..."
            decimalScale={0}
            allowLeadingZeros
            value={deposit?.referenceNumber}
            data-testid="depositcomponent-check-reference-input"
            onValueChange={({ value }) => {
              void onChangeRefNum(value, deposit.id);
            }}
          />
        </Grid.Col>
      </>
    );
  };

  const renderComponent = () => {
    return (
      <Box ml={"xs"} data-testid="depositcomponent-container">
        {hasWalletAmount && (
          <Box data-testid="depositcomponent-wallet-global-info">
            <small className={"secondary-color ms-1 d-block"}>Saldo disponible en bolsillo</small>
            <Text fw={700} c="brand-color" data-testid="depositcomponent-wallet-available-amount">
              <NumberFormatter prefix="$ " value={Number(walletAmount)} thousandSeparator />
            </Text>
          </Box>
        )}
        {depositList.map((deposit, index) => (
          <Card key={deposit.id} mb={"xs"} data-testid={`depositcomponent-deposit-card-${index}`}>
            <Box
              key={deposit.id}
              pos={"relative"}
              my={"xs"}
              p={"xs"}
              data-testid={`depositcomponent-deposit-box-${index}`}
            >
              {depositList.length > 1 && (
                <Box
                  pos={"absolute"}
                  className="position-absolute"
                  style={{ right: 10, top: 5 }}
                  onClick={() => onRemoveDeposit(deposit.id)}
                  data-testid={`depositcomponent-remove-button-${index}`}
                >
                  <IconX className="pointer" />
                </Box>
              )}
              <div className="flex w-100">
                <Card
                  style={{ minWidth: 100 }}
                  mr={"lg"}
                  data-testid={`depositcomponent-icon-card-${index}`}
                >
                  <Box p={"xs"} className={" h-100 p-3 flex items-center justify-center"}>
                    {icons[deposit.type]}
                  </Box>
                </Card>
                <Grid className="w-100 items-end">
                  <Grid.Col pb={0} span={6}>
                    <Select
                      className="text-secondary"
                      label="Tipo de recaudo"
                      required
                      placeholder={"Seleccionar..."}
                      data={depositTypes}
                      value={
                        formattedDataSelect(
                          depositTypesList.results,
                          "depoTypeName",
                          "depoTypeLabel",
                          ["depoTypeId"]
                        )?.find((type) => type.value === deposit.type)?.value
                      }
                      data-testid={`depositcomponent-type-select-${index}`}
                      onChange={(_, value: IOptionItem) => {
                        onChangeDepositType({
                          id: value.depoTypeId ?? "",
                          type: value.value,
                          deposit
                        });
                      }}
                    />
                  </Grid.Col>
                  {deposit.type === "default" && renderDefaultType()}
                  {deposit.type === "transfer" && renderTransferType(deposit)}
                  {deposit.type === "cash" && renderCashType(deposit)}
                  {deposit.type === "card" && renderCardType(deposit)}
                  {deposit.type === "debit_note" && renderNoteType(deposit)}
                  {deposit.type === "credit_note" && renderNoteType(deposit)}
                  {deposit.type === "check" && renderCheckType(deposit)}
                  {deposit.type === "wallet" && renderWalletType(deposit)}
                </Grid>
              </div>
            </Box>
          </Card>
        ))}
        <Box
          px={""}
          className="flex items-center "
          data-testid="depositcomponent-actions-container"
        >
          {payment - totalPayment > 0 && (
            <Button
              className={""}
              onClick={onAddNewDeposit}
              data-testid="depositcomponent-add-deposit-button"
            >
              <b className={"text-muted me-2"}>Agregar otro recaudo</b> &nbsp;
              <IconCirclePlus className="pointer " />
            </Button>
          )}

          <Box
            p={"sm"}
            className={` gap-3 flex ${Number(totalPayment) < payment ? "danger-color" : "primary-color"}`}
            data-testid="depositcomponent-pending-amount-display"
          >
            <b>Faltante</b>

            <Text
              fz="md"
              flex={1}
              c={` ${Number(totalPayment) < payment ? "danger-color" : "primary-color"}`}
              fw={"bold"}
            >
              <NumberFormatter
                prefix="$ "
                value={Number(totalPayment) < payment ? payment - totalPayment : 0}
                thousandSeparator
              />{" "}
            </Text>
          </Box>
        </Box>
      </Box>
    );
  };

  return renderComponent();
}
